import { useFerve } from "@features/ferve/FerveProvider"
import { useAppData, useRPCQuery } from "eddev/hooks"

import { IconName } from "@components/ui/Icon"
import {
  EventStatus,
  FilmSessionFragment,
  FilmTileFragment,
  FilmTileWithSessionsFragment,
  SessionTicketsFragment,
} from "@generated-types"
import { isPastEvent } from "@utils/past-event"
import { useMemo } from "react"
import { useFerveMode } from "@features/ferve/useFerveMode"

type Args = {
  film?: FilmTileFragment | FilmTileWithSessionsFragment
  session?: FilmSessionFragment | SessionTicketsFragment
}

type BadgeType = "sold-out" | "selling-fast" | "past-event" | "free" | "guest" | "on-sale" | "label"

const BADGE_LABELS: { [K in BadgeType]: string } = {
  "sold-out": "Sold Out",
  "selling-fast": "Selling Fast",
  "past-event": "Past Event",
  free: "Free",
  guest: "Guest",
  "on-sale": "On Sale",
  label: "",
}

const BADGE_ICONS: { [K in BadgeType]?: IconName } = {
  guest: "guest",
}

export type FilmBadgeData = {
  label: string
  type: BadgeType
  icon?: IconName
}

export function useProgramStatuses() {
  const sessions = useRPCQuery("api.ferve.statusManifest", undefined, {
    staleTime: 2 * 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    networkMode: "always",
    structuralSharing: true,
  })
  return sessions.data
}

/**
 * For a given film (and optional session), returns an array of badges to display on that film or session.
 *
 * Uses the realtime data from useProgramStatus where available.
 */
export function useFilmBadges(args: Args): FilmBadgeData[] {
  const statusMap = useProgramStatuses()
  const enableOnSaleTags = useAppData((d) => !!d.sffSettings?.festivalSettings?.enableOnSaleTags)

  return useMemo(() => {
    const badges = new Set<BadgeType>()
    const extraBadges: FilmBadgeData[] = []
    let isPast = isPastEvent(args)

    /** We directly mutate the session and film objects to update their status */
    if (statusMap) {
      if (args.session?.info?.ferveId) {
        args.session.info.status = statusMap.get(args.session.info.ferveId)?.status || args.session.info.status
      }
      if (args.film?.sessions?.length) {
        for (const session of args.film.sessions) {
          if (session?.info?.ferveId) {
            session.info.status = statusMap.get(session.info.ferveId)?.status || session.info.status
          }
        }
      }
    }

    if (args.film && args.film.eventInfo?.tags?.length! > 0 && !args.session) {
      for (const tag of args.film.eventInfo?.tags!) {
        extraBadges.push({
          type: "label",
          label: tag,
        })
      }
    }

    if (isPast) {
      badges.add("past-event")
    } else if (args.session) {
      const status = args.session.info?.status
      if (status === EventStatus.Free) {
        badges.add("free")
      } else if (status === EventStatus.SoldOut) {
        badges.add("sold-out")
      } else if (status === EventStatus.SellingFast) {
        badges.add("selling-fast")
      } else if (status === EventStatus.Normal) {
        badges.add("on-sale")
      }
      if (args.session.info?.tags?.length! > 0) {
        for (const tag of args.session.info?.tags!) {
          extraBadges.push({
            type: "label",
            label: tag,
          })
        }
      }
    } else if (args.film && args.film.sessions) {
      const allSoldOut = args.film.sessions.every((s) => s?.info?.status === EventStatus.SoldOut)
      const generallySellingFast = args.film.sessions.some(
        (s) => s?.info?.status === EventStatus.SoldOut || s?.info?.status === EventStatus.SellingFast,
      )
      const hasOnSale = args.film.sessions.some((s) => s?.info?.status === EventStatus.Normal)
      if (allSoldOut) {
        badges.add("sold-out")
      } else if (generallySellingFast) {
        badges.add("selling-fast")
      } else if (hasOnSale) {
        badges.add("on-sale")
      }
    }
    if (args.film?.hasGuest) {
      badges.add("guest")
    }

    if (!enableOnSaleTags) badges.delete("on-sale")

    return [...badges]
      .map(
        (badge): FilmBadgeData => ({
          label: BADGE_LABELS[badge],
          type: badge,
          icon: BADGE_ICONS[badge],
        }),
      )
      .concat(extraBadges)
  }, [args.film, args.session, statusMap])
}
